import { ComponentProps, ReactNode } from 'react';
import { Flag, Icon } from '@opswat/ui';
import { LimitInterval } from 'services/api/NotificationService';
import { ScanResultCode } from 'types/Workflows';

export type CountryCode = ComponentProps<typeof Flag>['countryCode'];

export interface UrlOverviewResources {
    verdict?: {
        //mdaas verdict
        threatLevel?: number;
        confidence?: number;
    };
    finalVerdict?: {
        threatLevel?: number;
        confidence?: number;
    };
    allSignalGroups?: unknown[];
    allTags?: Array<{ tag: { name: string } }>;
}

export interface RequestData {
    initiatorInfo: {
        type: string;
        url?: string;
        host?: string;
    };
    request: {
        documentURL: string;
        frameId: string;
        hasUserGesture: boolean;
        initiator: {
            type: string;
        };
        loaderId: string;
        primaryRequest: boolean;
        redirectHasExtraInfo: boolean;
        redirectResponse?: {
            alternateProtocolUsage: string;
            charset: string;
            connectionId: number;
            connectionReused: boolean;
            encodedDataLength: number;
        };
        request: {
            headers: Record<string, string>;
            initialPriority: string;
            isSameSite: boolean;
            method: string;
            mixedContentType: string;
            referrerPolicy: string;
            url: string;
        };
        requestId: string;
        timestamp: number;
        type: string;
        wallTime: number;
    };
    response: {
        frameId: string;
        hasExtraInfo: boolean;
        loaderId: string;
        requestId: string;
        dataLength?: number;
        response: {
            mimeType?: string;
            status?: number;
            statusText?: string;
            headers?: Record<string, string>;
            connectionId?: number;
            remoteIPAddress?: string;
            remotePort?: number;
            fromDiskCache?: boolean;
            fromServiceWorker?: boolean;
            encodedDataLength?: number;
            timing?: {
                requestTime: number;
                proxyStart: number;
                proxyEnd: number;
                dnsStart: number;
                dnsEnd: number;
                connectStart: number;
                connectEnd: number;
                sslStart: number;
                sslEnd: number;
                workerStart: number;
                workerReady: number;
                workerFetchStart: number;
                workerRespondWithSettled: number;
                sendStart: number;
                sendEnd: number;
                pushStart: number;
                pushEnd: number;
                receiveHeadersEnd: number;
            };
        };
        timestamp: number;
    };
}

export interface ScanDetail {
    def_time: string;
    location: string;
    scan_result_i: number;
    scan_time: number;
    threat_found: string;
}

export interface ScanResults {
    scan_details: {
        [engineName: string]: ScanDetail;
    };
    scan_all_result_i: ScanResultCode | number;
    current_av_result_i: number;
    start_time: string;
    total_time: number;
    total_avs: number;
    total_detected_avs: number;
    progress_percentage: number;
    scan_all_result_a: string;
    current_av_result_a: string;
    scan_results: ScanResults[];
}

export interface FileInfo {
    display_name?: string;
    file_type?: string;
    file_size: number;
    upload_timestamp: string;
    md5: string;
    sha1: string;
    sha256: string;
    file_type_category: string;
    file_type_description: string;
    file_type_extension: string;
    dlp_info?: DlpInfo;
    sanitized?: Sanitized;
    process_info?: ProcessInfo;
}

export interface Sanitized {
    result: string;
    progress_percentage: number;
    file_path?: string;
    data_id: string;
    reason: string;
    file_expired: boolean;
}

export interface ScanHistoryObject {
    apikey?: string;
    organization_id?: string;
    nextCursor?: string;
    data_id: string;
    file_info?: FileInfo;
    scan_results?: ScanResults;
    workflow?: string;
    organization?: string;
    process_info?: {
        processing_time?: string;
        result?: string;
    };
    scan_with: string;
    sanitized: Sanitized;
    sandbox?: SandboxData;
    dlp_info?: DlpInfo;
}

export type TableHeaderType = {
    label: string;
    align?: 'right' | 'left';
    className?: string;
};

export interface FileHashResultsProps {
    id: string;
    scanType: string;
}

export interface SandboxResource {
    name?: string;
    type?: string;
    size?: number;
    md5?: string;
    sha1?: string;
    sha256?: string;
    created_time?: string;
    modified_time?: string;
    accessed_time?: string;
    description?: string;
    verdict?: string;
    threat_level?: number;
    confidence?: number;
    category?: string;
    file_type?: string;
    status?: string;
    properties?: Record<string, unknown>;
    extendedData?: ExtendedData;
    metaData?: MetaData;
    verinfo?: VerInfo;
    [key: string]: string | number | boolean | undefined | Record<string, unknown> | ExtendedData | MetaData | VerInfo;
}

export interface SandboxFinalVerdict {
    verdict?: string;
    confidence?: number;
    threatLevel?: number;
    threat_level?: number;
    blocked?: boolean;
}

export interface SandboxReport {
    finalVerdict?: {
        verdict?: string;
        confidence?: number;
        threatLevel?: number;
    };
    resources: {
        [key: string]: SandboxResource;
    };
    reports?: {
        [key: string]: {
            finalVerdict?: SandboxFinalVerdict;
            allSignalGroups?: unknown[];
            allTags?: Array<{ tag: { name: string } }>;
            emulationGraph?: unknown;
            [key: string]: unknown;
        };
    };
}

export interface SandboxReports {
    [key: string]: SandboxReport;
}

export interface SandboxData {
    final_verdict: {
        blocked: boolean;
        confidence: number;
        threat_level?: number;
        threatLevel?: number;
        verdict: string;
    };
    full_report: string | Record<string, unknown>;
    overview_report: {
        analysis_state: string;
        chat_gpt_summary: string;
        error_messages: never[];
    };
    report_url: string;
    result_template_hash: string;
    reports?: SandboxReports;
    verdict: string;
}

export interface SandboxSignalGroup {
    allTags: Array<{
        isRootTag: boolean;
        source: string;
        sourceIdentifier: string;
        tag: {
            descriptions: string[];
            name: string;
        };
    }>;
    // When available, raw signals for origin extraction
    signals?: SandboxEmulationEvent[];
    allMitreTechniques?: Array<{
        ID: string;
        name: string;
        relatedTactic?: {
            ID: string;
            name: string;
        };
    }>;
    averageSignalStrength: number;
    description: string;
    finalSignalStrength: number;
    identifier: string;
    peakSignalStrength: number;
    /** URL sandbox `signal_groups` often use a string verdict; file reports use an object. */
    verdict:
        | string
        | {
              confidence: number;
              threatLevel: number;
              verdict: string;
          };
}

export interface SandboxYaraMatch {
    rule: string;
    strings: string[];
    tags: string[];
}

export interface YaraTag {
    tag?: {
        name?: string;
    };
    source?: string;
}

export interface YaraRuleData {
    ruleName: string;
    tags: (string | YaraTag)[];
    description: string;
    verdict: {
        confidence: number;
        threatLevel: number;
        verdict: string;
    };
    metaData: {
        author?: string;
        date?: string;
        description?: string;
        reference?: string;
    };
    matchedStrings: string[];
    strength: number;
}

export interface SandboxEmulationEvent {
    action: string;
    dataUUID: string;
    additionalInformation: {
        Pid?: string;
        ParentPid?: string;
        ProcessName?: string;
        FileName?: string;
        URL?: string;
        Program?: string;
        [key: string]: unknown;
    };
    strength?: number;
    signalReadable?: string;
    originPath?: string;
    originType?: string;
    originIdentifier?: string;
}

export interface SandboxStringOrigin {
    identifier: string;
    type: string;
    mediaType?: object;
}

export interface SandboxStringReferenceMetadata {
    emulationEventIDs: string[];
    reason: string[];
    triggeredConsumerIDs: string[];
}

export interface SandboxStringReference {
    dataUUID: string;
    interesting: boolean;
    metaData: SandboxStringReferenceMetadata;
    str: string;
    type: string;
}

export interface SandboxString {
    origin: SandboxStringOrigin;
    references: SandboxStringReference[];
}

export type SandboxGeoInfo = {
    country?: { name?: string };
    city?: { name?: string };
    location?: { latitude?: number; longitude?: number };
    subdivisions?: Array<{ name?: string }>;
};

export type SandboxResultData = {
    geo_info?: SandboxGeoInfo;
    [key: string]: unknown;
};

export type SandboxResultItem = Record<string, unknown> & {
    data?: SandboxResultData;
};

export interface SandboxResources {
    signalGroups?: SandboxSignalGroup[][];
    yaraMatches?: SandboxYaraMatch[][];
    emulationData?: SandboxEmulationEvent[][] | SandboxEmulationEvent[];
    fileDownloadResults?: unknown[];
    extractedFiles?: unknown[][];
    strings?: SandboxString[];
    extractedUrls?: unknown[];
    extractedIPs?: unknown[];
    extractedDomains?: unknown[];
    extractedEmails?: unknown[];
    extractedHashesMD5?: unknown[];
    extractedHashesSHA1?: unknown[];
    extractedHashesSHA256?: unknown[];
    extractedHashesSHA512?: unknown[];
    extractedUUIDs?: unknown[];
    extractedRegistryPathways?: unknown[];
    extractedRevisionSaveIDs?: unknown[];
    results?: SandboxResultItem[][];
    [key: string]: unknown;

    extendedData?: ExtendedData[];
    digests?: Digest[];
    visualizedSample?: VisualizedSample[];
    certInfos?: SandboxCertificateInfo[][];
    metaData?: unknown[];
    fileSize?: number;

    dieInfo?: DieInfo[][];
}

export interface SandboxCertificateInfo {
    owner?: string;
    issuer?: string;
    serialNumber?: string;
    validFrom?: string;
    validUntil?: string;
    MD5?: string;
    SHA1?: string;
    isExpired?: boolean;
    isRevoked?: boolean;
    isSigningChainMember?: boolean;
    isSelfSigned?: boolean;
    isInvalid?: boolean;
    isRoot?: boolean;
    isShortLived?: boolean;
    PublicKey?: string;
    PublicKeyAlgorithm?: string;
}

export interface EntrypointName {
    imageBase: string;
    entrypointEntropy: string;
    entrypointVA: string;
    crcInFile: string;
    crcActual: string;
}

export interface Stream {
    name: string;
    size: string;
    filename?: string;
    vbaFilename?: string;
    vbaCode?: string;
}

export interface DieInfo {
    type: string;
    name: string;
    version: string;
}

export interface PdbData {
    age: string;
    pdbPath: string;
    size: string;
    guidData: GuidData;
    guidData1?: string;
    guidData2?: string;
    guidData3?: string;
    guidData4?: string;
    timestamp: string;
}

export interface GuidData {
    guidData1: string;
    guidData2: string;
    guidData3: string;
    guidData4: string;
}

export interface SandboxImportItem {
    name?: string;
    fileRva?: string;
    suspicious?: boolean;
    isUndocumented?: boolean;
    ordinal?: string;
    allMitreTechniquesAsString?: string;
}

export interface SandboxImportModule {
    module?: string;
    imports?: SandboxImportItem[];
}

export interface Resource {
    name?: string;
    size?: string;
    type?: string;
    id?: string;
    rva?: string;
    fileRva?: string;
    sha256?: string;
    fileMagicDescription?: string;
    lang?: string;
    sublang?: string;
}

export interface Section {
    name?: string;
    size?: string;
    va?: string;
    virtualSize?: string;
    fileRva?: string;
    sizeOfRawData?: string;
    md5?: string;
    entropy?: string;
    characteristics?: string;
    characteristicsReadable?: string[];
}

export interface RichHeaderItem {
    checksum?: string;
    clearData?: string;
    csum_calc?: string;
    csum_file?: string;
    error?: string;
    key?: string;
    offset?: string;
    rawData?: string;
}

export type RichHeader = RichHeaderItem[];

export interface Header {
    name: string;
    size: string;
}

export interface Characteristics {
    dllCharacteristicsReadable: string;
    fileCharacteristicsReadable: string;
    dllCharacteristics: string;
    fileCharacteristics: string;
}

export interface Export {
    name?: string;
    suspicious?: boolean;
    va?: string;
    ordinal?: string;
}

export interface ExtendedData {
    entrypointName?: string;
    streams?: Stream[];
    pdbData?: PdbData[];
    dllCharacteristicsReadable?: string[];
    fileCharacteristicsReadable?: string[];
    dllCharacteristics?: string;
    fileCharacteristics?: string;
    importsEx?: SandboxImportModule[];
    resources?: Resource[];
    sections?: Section[];
    verinfo?: VerInfo | VerInfo[];
    richHeader?: RichHeader;
    richHeaderEx?: RichHeader;
    header?: Header;

    imports?: SandboxImportModule[];
    exports?: Export[];

    // Additional properties from the code
    appVersion?: string;
    application?: string;
    company?: string;
    contentType?: string;
    docSecurityString?: string;
    template?: string;
    cpRevision?: string;
    dcPublisher?: string;
    dcTermsCreated?: string;
    dcTermsModified?: string;
    entrypointEntropy?: string;
    fileModifiedTimestamps?: string;
    isRepaired?: boolean;
    languageCodes?: string;
    characterCount?: string;
    characterCountWithSpaces?: string;
    lineCount?: string;
    pageCount?: string;
    paragraphCount?: string;
    wordCount?: string;
    xmtpNPages?: string;

    // Header properties
    imageBase?: string;
    entrypointVA?: string;
    crcInFile?: string;
    crcActual?: string;
    linkerVersionMajor?: string;
    linkerVersionMinor?: string;
    compilerFlags?: {
        IsASLR?: boolean;
        IsNXCOMPAT?: boolean;
        IsSEH?: boolean;
        IsCFG?: boolean;
        IsGS?: boolean;
    };
    pointerToSymbolTable?: string;
    numberOfSymbols?: string;

    // File details properties
    fileMagicDescription?: string;
    architecture?: string;
    subsystemReadable?: string;
    dates?: string | { dateUtc?: string };
    packers?: string[];
    compilers?: string;
    imphash?: string;
    fsiofuzzyhash?: string;
    authentihash?: string;
    ssdeep?: string;
    isDigitallySigned?: boolean;
    isDotNet?: boolean;
    isPacked?: boolean;
    icon?: string;

    /** URL / URL-to-file sandbox report (names vary by API; keep optional) */
    originalUrl?: string;
    OriginalUrl?: string;
    originalUrlIdentifier?: string;
    OriginalUrlIdentifier?: string;
    forensicAnalysisRecommended?: boolean;
    ForensicAnalysisRecommended?: boolean;
    isPureUrlAnalysis?: boolean;
    IsPureUrlAnalysis?: boolean;
    isUrlToFileAnalysis?: boolean;
    IsUrlToFileAnalysis?: boolean;
    mlBrandDetection?: boolean | string;
    mlWebThreatModel?: string;
    mlWebThreatModelUrl?: string;
    mlWebThreatModelReportUrl?: string;
    checkDurationMs?: number;
    originalScreenshotUrl?: string;
    OriginalScreenshotUrl?: string;
    original_screenshot_url?: string;
    brandDetectionScreenshotUrl?: string;
    BrandDetectionScreenshotUrl?: string;
    brand_detection_screenshot_url?: string;
}

export interface Digest {
    [key: string]: string;
}

export interface VisualizedSample {
    base64?: string;
    [key: string]: string | undefined;
}
export interface SandboxExtractedStringEntry {
    name: string;
    type: string;
    origin: string;
    interesting: boolean;
    metaData?: object;
    mediaType?: object;
}

export interface ExtractedFileData {
    data: string;
    origin: string;
    type: string;
    properties?: ExtractedFileItem;
}

export interface ExtractedFileDataWithTags extends ExtractedFileData {
    tags: Array<{
        name: string;
        isRootTag?: boolean;
        source?: string;
    }>;
}

export interface ExtractedFilesSandboxProps {
    sandboxResources?: SandboxResources;
    sandboxErrorr?: boolean;
}

export interface ExtractedFileItem {
    metaData?: {
        name?: string;
    };
    digests?: {
        MD5?: string;
        SHA1?: string;
        'SHA-1'?: string;
        SHA256?: string;
        'SHA-256'?: string;
        SHA512?: string;
        'SHA-512'?: string;
    };
    fileSize?: number;
    mediaType?: {
        string?: string;
    };
    origin?: {
        type?: string;
    };
    allTags?: Array<{
        isRootTag?: boolean;
        source?: string;
        sourceIdentifier?: string;
        tag?: {
            descriptions?: string[];
            name?: string;
        };
    }>;
    [key: string]: unknown;
}

export type VerdictVariant =
    | 'failed'
    | 'success'
    | 'neutral'
    | 'info'
    | 'inProgress'
    | 'inactive'
    | 'red'
    | 'orange'
    | 'yellow'
    | 'green'
    | 'alert'
    | 'warn';
export interface Verdict {
    variant: VerdictVariant;
    label: string;
    color: string;
}

export interface VerdictObject {
    variant: VerdictVariant;
    label?: string;
    icon?: string;
}

export interface BadgeProps {
    label: string;
    variant: VerdictVariant;
}

export interface SanitizedResult {
    action: string;
    count: number;
    object_name: string;
    object_sha256: string[];
    object_details?: unknown;
}

export interface SanitizationDetail {
    action: string;
    object_name: string;
}

export interface VerdictTag {
    verdict: string;
    variant: VerdictVariant;
}

export interface TagWithVerdict {
    tag: {
        name: string;
        verdict: {
            verdict: string;
        };
    };
}

export interface SandboxBadgeProps {
    label: string;
    variant: VerdictVariant;
}

export type GetMultiscanVerdict = (scanResults: ScanResults | undefined) => Verdict;
export type GetSanitizedVerdict = (sanitized: Sanitized | undefined) => BadgeProps;
export type GetSandboxVerdict = (verdict: string | undefined) => SandboxBadgeProps;
export type GetScanVerdict = (index: number) => BadgeProps;

export interface ProcessInfo {
    result: string;
    progress_percentage?: number;
    post_processing?: {
        sanitization_details?: {
            details?: SanitizedResult[];
        };
    };
    custom_workflow?: string;
}

export type StatusScanResults = Partial<Pick<ScanResults, 'scan_all_result_i' | 'scan_all_result_a'>>;

export interface StatusItem {
    process_info?: ProcessInfo;
    scan_results?: StatusScanResults;
}

interface DlpCategory {
    display_name: string;
    hits: DlpHit[];
}

export interface DlpInfo {
    hits: {
        ccn?: DlpCategory;
        cidr?: DlpCategory;
        ipv4?: DlpCategory;
        ssn?: DlpCategory;
        govid?: DlpCategory;
        adult?: DlpCategory;
        pid?: DlpCategory;
        secrets?: DlpCategory;
        regex?: DlpCategory;
        secret_ibm_cos_hmac_access_key?: DlpCategory;
        secret_ibm_api_connect_credential_id?: DlpCategory;
    };
    redact?: {
        result: string;
    };
    anonymization?: {
        result: string;
    };
    certainty?: string;
    crop_embedded_images?: {
        result: string;
    };
    current_file_certainty?: string;
    current_file_verdict?: number;
    verdict?: number;
    detection_policy?: {
        result: string;
    };
    watermark?: {
        result: string;
    };
}

export interface Tag {
    tag: {
        name: string;
        verdict: {
            verdict: string;
        };
    };
}

export interface FileInfoProps {
    fileInfo?: FileInfo;
    uploadTimestamp?: string;
    processingTime?: number;
    scanResults?: ScanResults;
    sandboxData?: SandboxData;
    sanitized?: Sanitized;
    processInfo?: ProcessInfo;
    dlpInfo?: DlpInfo;
    totalDlpHits: number;
    allTags: Tag[];
    sanitizedResults?: SanitizedResult[];
    sandboxError?: boolean;
    fileData?: FileResponseInterface;
}

export interface StatusVerdict {
    variant: 'allowed' | 'blocked' | 'neutral';
    label: string;
}

export interface MetascanTabProps {
    processInfo?: ProcessInfo;
    sanitized: Sanitized;
    sanitizedResults?: SanitizedResult[];
}

export interface TabsResultsProps {
    activeTab: string;
    onTabChange: (tab: string) => void;
    scanResults?: ScanResults;
    sanitized?: Sanitized;
    sanitizedResults?: SanitizedResult[];
    fileInfo?: FileInfo;
    fileData?: FileResponseInterface;
}

interface LastSandboxId {
    sandbox_id: string;
    system: string;
    date: string;
}

interface ExtractedFiles {
    files_in_archive: ExtractedFile[];
}

interface ExtractedFile {
    data_id: string;
    detected_by: number;
    display_name: string;
    file_size: number;
    file_type: string;
    progress_percentage: number;
    scan_result_i: number;
}

export interface FileResponseInterface {
    file_size: number;
    upload_timestamp: string;
    md5: string;
    sha1: string;
    sha256: string;
    file_type_category: string;
    file_type_description: string;
    file_type_extension: string;
    display_name: string;
    sandbox_id?: string;
    last_sandbox_id?: LastSandboxId[];
    votes: {
        up: number;
        down: number;
    };
    last_start_time: string;
    scan_result_history_length: number;
    file_id: string;
    data_id: string;
    coo: string;
    parent_data_id: string;
    sanitized: Sanitized;
    process_info: ProcessInfo;
    scan_results: ScanResults;
    file_info: FileInfo;
    share_file: number;
    private_processing: number;
    rest_version: string;
    dlp_info: DlpInfo;
    additional_info: string[];
    stored: boolean;
    extracted_files: ExtractedFiles;
    redact: string;
    newer: string;
    rescan_new_id: string;
    rescan_parent_id: string;
    coo_info?: CooInfo;
    aigcd_info?: AigcdInfo;
}

export interface CooInfo {
    overview_report?: OverviewReport;
}

export interface OverviewReport {
    country_name: string;
    country_code: CountryCode;
}
export interface FileUpload {
    data_id: string;
    sandbox_error: {
        messages: string[];
    };
    sha1: string;
    sandbox_id: string;
    last_sandbox_id?: Array<{
        sandbox_id: string;
    }>;
}

export interface SandboxResponseInterface {
    final_verdict: {
        verdict: string;
        threatLevel: number;
    };
    sandbox_id: string;
    store_at: string;
    scan_in_progress: string;
    scan_results: ScanResults;

    full_report: {
        json: string;
        pdf: string;
        html: string;
    };
    sadboxData: SandboxData;
    sandboxReport: SandboxReport;
}

export type GetDlpVerdict = (dlpInfo: DlpInfo | undefined) => VerdictObject;

// Workflows - DLP UI state types (used by `components/workflows/engines/Dlp.tsx`)
export type DlpProfileState = {
    profileName: string;
    profileDescription: string;
    profileAvailable: boolean;
};

export type DlpGeneralState = {
    timeoutValue: string;
    timeoutUnit: string;
    retries: string;
    outputFilenameFormat: string;
    detectEncoding: boolean;
    detectEncodingFallback: string;
};

export type DlpDetectionGeneralState = {
    ocr: boolean;
    recursiveDetection: boolean;
    recursiveDepthLimit: string;
    recursiveFileLimit: string;
    recursiveRemoveOnError: boolean;
    hitLimit: string;
};

export type DlpSecretDetectionState = {
    cloudProviders: string[];
    dbAccess: string[];
    generic: string[];
};

export type DlpPersonalDocumentState = {
    personalDocuments: boolean;
    identificationDocuments: boolean;
    animalAndPetDocuments: boolean;
    employmentDocuments: boolean;
    financialDocuments: boolean;
    energyAndUtilityDocuments: boolean;
    governmentAndTaxDocuments: boolean;
};

export type DlpExportGeneralState = {
    removeMetadata: boolean;
    removeAlternateImages: boolean;
};

export type DlpAnonymizationState = {
    anonymization: boolean;
    dicom: boolean;
    pcap: boolean;
};

export type DlpWatermarkState = {
    watermark: boolean;
    text: string;
    position: string;
    opacity: string;
};

export interface DlpHit {
    after: string;
    before: string;
    certainty: string;
    certainty_score: number;
    hit: string;
    location: string;
    severity: number;
    tryRedact: boolean;
    redacted?: boolean;
}

export interface SearchState {
    search: string;
    debouncedSearch: string;
    isTyping: boolean;
}

export interface TopItem {
    key: string;
    doc_count: number;
}

export interface IpReputation {
    clean: number;
    found: number;
    missing: number;
    infected: number;
    top_clean_items: TopItem[];
    top_infected_items: TopItem[];
}

export interface FileReputation {
    clean: number;
    found: number;
    missing: number;
    infected: number;
}

export interface FileScan {
    deep_cdr?: {
        total_sanitized_ok: number;
        total_sanitized_failed: number;
    };
    allowed_files?: number;
    blocked_files?: number;
    scanned_files?: number;
    infected_files?: number;
    skipped_or_failed?: number;
    vulnerabilities?: {
        total_low: number;
        total_unknown: number;
        total_critical: number;
        total_detected: number;
        total_moderate: number;
        total_important: number;
    };
    filetypes_allowed?: TopItem[];
    filetypes_blocked?: TopItem[];
    filetypes_scanned?: TopItem[];
    filetypes_infected?: TopItem[];
}

export interface StatisticPeriod {
    from: string;
    to: string;
    ip_reputation: IpReputation;
    file_reputation: FileReputation;
    file_scan?: FileScan;
}

export interface StatisticsData {
    statistics: StatisticPeriod[];
}

export interface StatisticsFileType {
    statistics: {
        file_scan: FileScan;
    };
}

export interface Organization {
    organization_id?: string;
    limit_feed?: number;
    limit_prevention?: number;
    limit_reputation?: number;
    limit_sandbox?: number;
    expiration_date?: string;
    ip_whitelist?: {
        v4: string[];
        v6: string[];
    };
    licenseLimitsTimeInterval?: LimitInterval;
    file_scan_thresholds?: number[];
    scan_result_codes?: ScanResultCode[];
    mdc_license_type?: LicenseType;
    throttling_limit?: number;
    max_upload_file_size?: number;
    max_archive_file_size?: number;
    max_archive_file_number?: number;
    max_archive_file_depth?: number;
    qos_scan?: string;
    sandbox_qos_scan?: string;
    ref_token?: string;
    enforce_private_scan?: boolean;
}

export enum LicenseType {
    Commercial = 'commercial',
    Community = 'community',
    Enterprise = 'enterprise',
    Internal = 'internal',
    Professional = 'professional',
    Standard = 'standard',
}

export interface Apikey {
    apikey?: string;
    throttling_limit?: number;
    max_upload_file_size?: number;
    max_archive_file_size?: number;
    max_archive_file_depth?: number;
    max_archive_file_number?: number;
    max_file_download?: number;
    max_custom_workflows?: number;
    limit_prevention?: number;
    limit_reputation?: number;
    limit_threat_intel_search?: number;
    limit_sandbox?: number;
    limit_feed?: number;
    qos_scan?: string;
    time_interval?: LimitInterval;
    file_scan_thresholds?: number[];
    scan_result_codes?: ScanResultCode[];
    portal_api_key?: string;
    public_key?: string;
    created_at?: string;
    nickname?: string;
    sandbox_qos_scan?: string;
    expiration_date?: string;
    vulnerability_submissions?: {
        fileName?: string;
        hash?: string;
        isCVE?: boolean;
        status?: string;
        created_at?: string;
        updated_at?: string;
    }[];
    is_enterprise?: boolean;
    mtls_only?: boolean;
    is_encrypt_enable?: boolean;
    workflow_rule?: number;
    enforce_private_scan?: boolean;
    blocked_avs?: unknown[];
    partner?: number;
    source?: string;
    sso_user_id?: string;
    userid?: string;
    license_change_note?: string;
    mdc_license_type?: LicenseType;
    updated_at?: string;
    csfs?: {
        userName?: string;
        loginUrl?: string;
        clientKey?: string;
        privateKey?: string;
    };
    organization?: Organization;
    isOnlineStore?: boolean;
    paid_user?: number;
    license?: {
        subscription_id?: string;
        created_at?: string;
        purchase_information?: {
            plan_id?: string;
            amount?: number;
            type?: string;
            tier?: string;
            currency?: string;
            nickname?: string;
            _id?: string;
        }[];
        invoice_url?: string;
        period_end?: string;
        period_start?: string;
        receipt_url?: string;
        canceled_at?: string;
    };
}

export interface User extends Apikey {
    email?: string;
    firstName?: string;
    lastName?: string;
    org_role?: 'user' | 'admin' | 'analyst' | null;
    isLoggedIn?: boolean;
    ipRestriction?: boolean;
    licenseInfo?: {
        paidUser?: number;
        licenseType?: LicenseType;
        expirationDate?: string;
    };
    license?: {
        period_end?: string;
    };
}

export interface Sso extends Partial<User> {
    apikey?: string;
}
export interface CsrfHeader {
    token: string;
    sso: Sso;
}

interface FileTypeItem {
    type: string;
    percentage: number;
    count: number;
}

interface FileTypeCategory {
    total: number;
    byType: FileTypeItem[];
}

export interface FileTypeStatistics {
    scanned: FileTypeCategory;
    allowed: FileTypeCategory;
    blocked: FileTypeCategory;
    infected: FileTypeCategory;
}

export interface ServiceResult {
    serviceResponse: string;
    result: string;
    count: number;
}

export interface Detection {
    verdict: string;
    description: string;
    count: number;
}
export interface FileSanitizedCategory {
    name: string;
    count?: number;
}

export interface FileSanitizedApiData {
    categories?: FileSanitizedCategory[];
    totalFiles?: number;
}

export interface StatisticsApiData {
    from: string;
    to: string;
    statistics: {
        file_scan: FileScan;
        dlp: {
            total_files_with_dlp: number;
        };
    };
}
export interface FileDLPCategory {
    name: string;
    count?: number;
}

export interface FileDLPApiData {
    categories?: FileDLPCategory[];
    totalFiles?: number;
}

export interface FileThreatsDetectedCategory {
    name: string;
    count?: number;
}

export interface FileThreatsDetectedApiData {
    categories?: FileThreatsDetectedCategory[];
    totalFiles?: number;
}

export interface FileVerdictsDetectedCategory {
    name: string;
    count?: number;
}

export interface FileVerdictsDetectedApiData {
    categories?: FileVerdictsDetectedCategory[];
}

export interface FileTypeByVerdictsTopFileType {
    extension: string;
    count: number;
}

export interface FileTypeByVerdictsCategory {
    totalFiles: number;
    topFileTypes: FileTypeByVerdictsTopFileType[];
}

export interface FileTypeByVerdictsApiData {
    categories?: Record<string, FileTypeByVerdictsCategory>;
}

export interface FileSanitizedStats {
    byType: {
        type: string;
        count: number;
    }[];
    total?: number;
}

export interface FileDLPStats {
    byType: {
        type: string;
        count: number;
    }[];
    total?: number;
}

export interface FileThreatsDetectedStats {
    byType: {
        type: string;
        count: number;
    }[];
}

export interface SecurityDashboardProps {
    serviceType: string;
}

export enum FileDetailsTabs {
    Overview = 'overview',
    ThreatsPrevented = 'threats-prevented',
    AdvancedAnalysis = 'advanced-analysis',
    DataLossPrevented = 'data-loss-prevented',
    Sanitization = 'sanitization',
    AiContentInspector = 'ai-content-inspector',
}

export interface AigcdDetectorMetric {
    display_name?: string;
    value?: number | string | boolean;
}

export interface AigcdDetector {
    display_name?: string;
    confidence?: number;
    metrics?: Record<string, AigcdDetectorMetric>;
    output_images?: Record<string, { display_name?: string; file_name?: string; size?: { width?: number; height?: number } }>;
}

export interface AigcdFeatureResult {
    display_name?: string;
    result?: string;
    ai_probability?: number;
    input_image_size?: { width?: number; height?: number };
    detectors?: Record<string, AigcdDetector>;
    details?: {
        document_type?: string;
        document_type_display_name?: string;
        document_type_confidence?: number;
        ai_probability?: number;
    };
}

export interface AigcdOutputImage {
    output_file_name?: string;
    image_alias?: string;
    file_url?: string;
}

export interface AigcdInfo {
    // err_code 6 === "Unsupported File Type"; final_verdict is absent in that case.
    err_code?: number;
    err_details?: string;
    final_verdict?: {
        verdict?: string;
        verdict_explanation?: string[];
    };
    result_description?: {
        feature_results?: {
            detect_image?: AigcdFeatureResult;
            detect_text?: AigcdFeatureResult;
            detect_fraud?: AigcdFeatureResult;
        };
        detection_details?: {
            threshold?: number;
        };
    };
    output_images?: AigcdOutputImage[];
    result_template_hash?: string;
    retried?: number;
}

export enum AdvancedAnalysisTabs {
    FileDetails = 'file-details',
    EmulationGraph = 'emulation-graph',
    YaraRules = 'yara-rules',
    ExtractedFiles = 'extracted-files',
    ExtractedStrings = 'extracted-strings',
    OsintLookups = 'osint-lookups',
}

export enum FileDetailsSandboxTabs {
    Meta = 'meta',
    OLEStreams = 'ole-streams',
    Exports = 'exports',
    Certificates = 'certificates',
    Resources = 'resources',
    Sections = 'sections',
    Verinfo = 'verinfo',
    RichHeader = 'rich header',
    Header = 'header',
    Characteristics = 'characteristics',
    PdbData = 'pdb-data',
    Imports = 'imports',
}

// Interfaces moved from Overview.tsx
export interface OverviewFileInfo {
    display_name?: string;
    file_size?: number;
    file_type_category?: string;
    file_type_description?: string;
    file_type_extension?: string;
    md5?: string;
    sha1?: string;
    sha256?: string;
    upload_timestamp?: string;
}

export interface ExtendedData {
    ssdeep?: string;
    architecture?: string;
    isDotNet?: boolean;
    isPacked?: boolean;
    isDigitallySigned?: boolean;
}

export interface MetaData {
    entropy?: number;
}

export interface VerInfo {
    CompanyName?: string;
    FileDescription?: string;
    FileVersion?: string;
    InternalName?: string;
    LegalCopyright?: string;
    ProductName?: string;
    ProductVersion?: string;
    name?: string;
    value?: string;
    map?: (callback: (item: unknown) => unknown) => unknown[];
}

export interface OverviewProps {
    fileData?: FileResponseInterface;
    sandboxResources?: SandboxResources;
    fileDataError?: boolean;
    sandboxReport?: SandboxReport;
    sandboxData?: SandboxResponseInterface;
    sandboxError?: boolean;
    sandboxId?: string;
}

export interface GeoData {
    country: {
        name: string;
    };
    location: {
        latitude: number;
        longitude: number;
    };
}

export interface IndicatorItemProps {
    icon: ComponentProps<typeof Icon>['iconName'];
    label: string;
    count: number;
    variant?: 'malicious' | 'likelyMalicious' | 'suspicious' | 'other' | 'neutral';
    bgIcon?: string;
    bgIconBg?: string;
}

export interface ThreatsPreventedProps {
    fileData?: FileResponseInterface;
    sandboxReport?: SandboxData;
    sandboxResources?: SandboxResources;
    sandboxError?: boolean;
    sandboxId?: string;
    sandboxData?: SandboxResponseInterface;
}

export interface CVEsInterface {
    updated_ts: string;
    severity: string;
    description: string;
    published_epoch: number;
    last_modified_epoch: number;
    hashes_count: number;
    sha256: string[];
    cvss_2_0: {
        score: number;
        authentication: string;
        integrity_impact: string;
        confidentiality_impact: string;
        availability_impact: string;
        access_vector: string;
        access_complexity: string;
        attack_complexity: string;
    };
    cvss_3_0: {
        base_score: number;
        base_severity: string;
        vector_string: string;
        attack_vector: string;
        integrity_impact: string;
        confidentiality_impact: string;
        availability_impact: string;
        attack_complexity: string;
        authentication: string;
        privileges_required: string;
        user_interaction: string;
        scope: string;
    };
    references: Array<{
        url: string;
        name: string;
    }>;
    opswat_product_info: Array<{
        vendor: { name: string };
    }>;
    resolution: Array<{
        product_name: string;
        higher_than_version: string;
        higher_than_or_equal_to_version: string;
    }>;
    product_info?: {
        product?: {
            name: string;
        };
    };
    cve: string;
    severity_index: string;
}

export interface VulnerabilityResponseInterface {
    cves: string[];
    vulnerability: {
        severity: string;
        severity_index: number;
    };
}

export interface VulnerabilityDataInterface {
    data: Array<CVEsInterface>;
}

export interface IpDomainUrlMultiscanResultsProps {
    results: {
        address: string;
        lookup_results: {
            detected_by: number;
            start_time: string;
            sources: Array<{
                provider: string;
                assessment: string;
                detect_time: string;
                status: number;
                update_time: string;
            }>;
        };
        geo_info: {
            city: { name: string };
            country: { name: string };
            location: { latitude: number; longitude: number };
            subdivisions: Array<{ name: string }>;
        };
    };
    scanType: string;
    isLoadingResults: boolean;
    isValidatingResults: boolean;
    resultsError?: unknown;
}

// Sandbox URL interfaces
export interface ReportData {
    reports?: Record<string, Report>;
    full_report?: string;
}

export interface Report {
    resources?: Record<string, Resource>;
    [key: string]: unknown;
}

export interface Resource {
    /** Nested shape from some exporters; bare array is also returned for URL sandbox (see MDCL-16771). */
    renderResults?: RenderResults | RenderResult[];
    [key: string]: unknown;
}

export interface RenderResults {
    renderResults?: RenderResult[];
    [key: string]: unknown;
}

export interface RenderResult {
    urlRenderData?: {
        result?: {
            lists?: {
                certificates?: Certificate[];
            };
            data?: {
                requests?: Record<string, Request>;
            };
            phishDetection?: PhishDetection;
        };
        phishDetectionState?: string;
    };
    base64?: string;
    [key: string]: unknown;
}

export interface Certificate {
    subjectName: string;
    issuer: string;
    validFrom: number;
    validTo: number;
}

export interface Request {
    [key: string]: unknown;
}

export interface PhishDetection {
    [key: string]: unknown;
}

export interface ParsedReport {
    resources?: Record<string, Resource>;
    [key: string]: unknown;
}

export interface YaraRulesSandboxProps {
    sandboxResources?: SandboxResources;
}

export interface AccessErrorProps {
    link?: boolean;
}

export interface VolumeHeatMapApiData {
    statistics: Array<{ from: string; to: string; volume: number }>;
}

type ExtractedStringRef = {
    str: string;
    type: string;
    interesting?: boolean;
    metaData?: unknown;
};

type ExtractedStringOrigin = {
    type: string;
    mediaType?: unknown;
};

export type ExtractedStringGroup = {
    origin: ExtractedStringOrigin;
    references: ExtractedStringRef[];
};

export type SortCell = { data?: unknown };

export type OsintLookupData = {
    resource?: string;
    type?: string;
    osintProvider?: string;
    verdict?: string;
    origin?: {
        type?: string;
        identifier?: string;
    };
    tags?: unknown[];
    data?: unknown;
} & Record<string, unknown>;

export type UnknownRecord = Record<string, unknown>;

export type FilterItem = {
    id: string;
    text: string | undefined;
    checked: boolean;
    disabled?: boolean;
    icon?: string;
};

export type LicensingTableType = 'default' | 'checkmark' | 'antivirus' | 'antivirus-checkmark';
type IconName = ComponentProps<typeof Icon>['iconName'];

export type LicensingFeatureRow = {
    title: string;
    helpText?: string;
    analysis: ReactNode;
    reputation: ReactNode;
    threatIntel: ReactNode;
};

export type AntivirusFeatureRow = LicensingFeatureRow & {
    icon: IconName;
};

// Workflows - Metascan engine UI props
export interface MetascanTimeoutsProps {
    isLoadingWorkflowConfig: boolean;
    perEngineValue: string;
    perEngineUnit: string;
    avRetries: string;
    onPerEngineValueChange: (value: string) => void;
    onPerEngineUnitChange: (unit: string) => void;
    onAvRetriesChange: (value: string) => void;
}

export interface MetascanFailureThresholdsProps {
    isLoadingWorkflowConfig: boolean;
    infectedThresholdEnabled: boolean;
    infectedThreshold: string;
    suspiciousThresholdEnabled: boolean;
    suspiciousThreshold: string;
    failureThresholdEnabled: boolean;
    failureThreshold: string;
    onInfectedThresholdEnabledChange: (enabled: boolean) => void;
    onInfectedThresholdChange: (value: string) => void;
    onSuspiciousThresholdEnabledChange: (enabled: boolean) => void;
    onSuspiciousThresholdChange: (value: string) => void;
    onFailureThresholdEnabledChange: (enabled: boolean) => void;
    onFailureThresholdChange: (value: string) => void;
}

export interface MetascanEngine {
    id: string;
    name: string;
}

export interface MetascanAntivirusEnginesProps {
    isLoadingWorkflowConfig: boolean;
    selectedEngines: string[];
    onSelectedEnginesChange: (engines: string[]) => void;
}

// Workflows - File Type Verification engine UI props
export type NotClassifyRule = { filetype: string; subFiletype: string };

export interface FileTypeAnalysisProps {
    isLoading: boolean;
    timeoutValue: string;
    timeoutUnit: string;
    onTimeoutValueChange: (value: string) => void;
    onTimeoutUnitChange: (unit: string) => void;
    filetypeMismatchEnabled: boolean;
    onFiletypeMismatchEnabledChange: (enabled: boolean) => void;
    mismatchExceptionExtensions: string[];
    onMismatchExceptionExtensionsChange: (extensions: string[]) => void;
    filetypeFallbackEnabled: boolean;
    onFiletypeFallbackEnabledChange: (enabled: boolean) => void;
    exceptWhenFiletypeNotEnabledCdr: boolean;
    onExceptWhenFiletypeNotEnabledCdrChange: (enabled: boolean) => void;
    acceptNonStandardZip: boolean;
    onAcceptNonStandardZipChange: (enabled: boolean) => void;
    notClassifyFiletypes: string[];
    onNotClassifyFiletypesChange: (values: string[]) => void;
    notClassifySubFiletypes: string[];
    onNotClassifySubFiletypesChange: (values: string[]) => void;
    detectEncryptedArchive: boolean;
    onDetectEncryptedArchiveChange: (enabled: boolean) => void;
    classifyWithML: boolean;
    onClassifyWithMLChange: (enabled: boolean) => void;
    distinguishZeroByteFiles: boolean;
    onDistinguishZeroByteFilesChange: (enabled: boolean) => void;
    includeFileDetails: boolean;
    onIncludeFileDetailsChange: (enabled: boolean) => void;
    includeFileDetailsMetadata: boolean;
    onIncludeFileDetailsMetadataChange: (enabled: boolean) => void;
    includeFileDetailsOthers: boolean;
    onIncludeFileDetailsOthersChange: (enabled: boolean) => void;
    exifAnalysis: boolean;
    onExifAnalysisChange: (enabled: boolean) => void;
    logLevel: string;
    onLogLevelChange: (value: string) => void;
}

export interface FileTypeTextBasedDetectionProps {
    isLoading: boolean;
    detectBase64EncodedText: boolean;
    onDetectBase64EncodedTextChange: (enabled: boolean) => void;
    base64MinimumSize: number | null;
    onBase64MinimumSizeChange: (value: number | null) => void;
    detectAsciiText: boolean;
    onDetectAsciiTextChange: (enabled: boolean) => void;
    detectShiftJisText: boolean;
    onDetectShiftJisTextChange: (enabled: boolean) => void;
    asciiTextNonPrintableThreshold: string;
    onAsciiTextNonPrintableThresholdChange: (value: string) => void;
}

export interface FileTypeFileHandlingProps {
    isLoading: boolean;
    allowEncryptedContent: boolean;
    onAllowEncryptedContentChange: (enabled: boolean) => void;
}

// Workflows - Deep CDR File Type Settings (advanced settings)
export type FileTypeId = 'pdf' | 'word' | 'excel' | 'powerpoint' | 'visio' | 'raster-image' | 'svg' | 'dwf';

export type SettingKind = 'bool' | 'bool01' | 'enum' | 'number';

export type SettingOption = { label: string; value: string };

export type SettingDef = {
    key: string;
    label: string;
    kind: SettingKind;
    options?: SettingOption[];
};

// Workflows - File Type Settings page (Deep CDR)
export type FileTypeSettingsLocationState = {
    fileTypeId?: FileTypeId;
    fileType?: {
        id: string;
        category: string;
        fileType: string;
        extensions: string;
        enabled: boolean;
    };
    profileId?: string;
};

export type FileTypeSettingsSectionDef = { title: string; settings: SettingDef[] };

export type FileTypeSettingsProps = {
    profileIdFromPath?: string;
};

export type GeneralState = {
    timeoutValue: string;
    timeoutUnit: string;
    retries: string;
    logLevel: string;
    generateHtmlReport: boolean | null;
    generatePdfReport: boolean | null;
    requestFullReport: boolean | null;
    customProfileEnabled: boolean | null;
};

export type AdvancedState = {
    analysisEarlyTermination: boolean | null;
    analysisOsint: boolean | null;
    analysisYara: boolean | null;
    analysisExtractStrings: boolean | null;
    detectionHashOsint: boolean | null;
    detectionExtractedFilesYara: boolean | null;
    detectionCertificates: boolean | null;
    osintLookupsBasic: boolean | null;
};

export type ProfileState = {
    profileName: string;
    profileDescription: string;
    profileAvailable: boolean;
};

export type GeneralProps = {
    isLoading: boolean;
    value: GeneralState;
    onChange: (patch: Partial<GeneralState>) => void;
};

export type AdvancedSettingsProps = {
    isLoading: boolean;
    value: AdvancedState;
    onChange: (patch: Partial<AdvancedState>) => void;
};

export type TimeoutsState = {
    fileDownloadValue: string;
    fileDownloadUnit: string;
    timeoutEnabled: boolean;
    timeoutValue: string;
    timeoutUnit: string;
    retriesEnabled: boolean;
    retriesValue: string;
};

export type HashesState = {
    skipGenerateHash: boolean;
};

export type SelectOption = {
    value: string;
    label: string;
};

export type ProcessingState = {
    extractionTimeoutValue: string;
    extractionTimeoutUnit: string;
    extractOfficeDocuments: boolean;
    maxRecursionLevel: string;
    maxNumberOfFiles: string;
    maxTotalFilesSizeValue: string;
    maxTotalFilesSizeUnit: string;
    outputFilenameFormat: string;
    retainPasswords: boolean;
    retainPasswordsFileTypes: string[];
    analysisMode: boolean;
    returnSanitizationDetails: boolean;
    returnProcessedObjectsDetails: boolean;
    binaryObjects: boolean;
    binaryObjectSizeLimit: string;
};

export type ProcessingProps = {
    isLoadingWorkflowConfig: boolean;
    extractionTimeoutValue: string;
    extractionTimeoutUnit: string;
    extractOfficeDocuments: boolean;
    maxRecursionLevel: string;
    maxNumberOfFiles: string;
    maxTotalFilesSizeValue: string;
    maxTotalFilesSizeUnit: string;
    onExtractionTimeoutValueChange: (value: string) => void;
    onExtractionTimeoutUnitChange: (value: string) => void;
    onExtractOfficeDocumentsChange: (value: boolean) => void;
    onMaxRecursionLevelChange: (value: string) => void;
    onMaxNumberOfFilesChange: (value: string) => void;
    onMaxTotalFilesSizeValueChange: (value: string) => void;
    onMaxTotalFilesSizeUnitChange: (value: string) => void;
};

// Workflows - Archive Extraction engine UI state (used by `components/workflows/engines/ArchiveExtraction.tsx`)
export type ArchiveExtractionProcessingState = {
    extractionTimeoutValue: string;
    extractionTimeoutUnit: string;
    extractOfficeDocuments: boolean;
    maxRecursionLevel: string;
    maxNumberOfFiles: string;
    maxTotalFilesSizeValue: string;
    maxTotalFilesSizeUnit: string;
    // Extraction Handling
    failInvalidFileStructure: boolean;
    failContainUnexpectedData: boolean;
    failExtractedPartially: boolean;
    failUnsupportedArchive: boolean;
    failUnextractable: boolean;
    failSystemErrors: boolean;
    failOtherExtractionErrors: boolean;
    failCliExtractionErrors: boolean;
    extractOnError: boolean;
    enforceContentTypeMatched: boolean;
    unsupportedFsBehavior: string;
};

export type AnalysisState = {
    timeoutValue: string;
    timeoutUnit: string;
    filetypeMismatchEnabled: boolean;
    mismatchExceptionExtensions: string[];
    specifiedDetectionEnabled: boolean;
    specifiedDetectionFileTypes: string[];
    specifiedDetectionGroups: string[];
    filetypeFallbackEnabled: boolean;
    exceptWhenFiletypeNotEnabledCdr: boolean;
    acceptNonStandardZip: boolean;
    notClassifyFiletypes: string[];
    notClassifySubFiletypes: string[];
    detectEncryptedArchive: boolean;
    classifyWithML: boolean;
    distinguishZeroByteFiles: boolean;
    includeFileDetails: boolean;
    includeFileDetailsMetadata: boolean;
    includeFileDetailsOthers: boolean;
    exifAnalysis: boolean;
    logLevel: string;
};

export type TextDetectionState = {
    detectBase64EncodedText: boolean;
    base64MinimumSize: number | null;
    detectAsciiText: boolean;
    detectShiftJisText: boolean;
    asciiTextNonPrintableThreshold: string;
};

export type FileHandlingState = {
    allowEncryptedContent: boolean;
};

export type LocalState = {
    filetypeConfigFromBe: Record<string, unknown> | null;
};

export type EventEmitter = (event: string, payload?: unknown) => void;
export type DeepCDRProps = {
    profileID?: string;
    onEvent?: EventEmitter;
};

export type PerformanceState = {
    sanitizationValue: string;
    sanitizationUnit: string;
    encryptionValue: string;
    encryptionUnit: string;
    retries: string;
};

export type BlockHandlingState = {
    scriptObjects: boolean;
    scriptObjectsFileTypes: string[];
    hyperlinks: boolean;
    hyperlinksFileTypes: string[];
    externalContent: boolean;
    externalContentFileTypes: string[];
    embeddedObjects: boolean;
    embeddedObjectsFileTypes: string[];
    suspiciousObjects: boolean;
    suspiciousObjectsFileTypes: string[];
    qrCode: boolean;
    qrCodeFileTypes: string[];
    invalidSignature: boolean;
    invalidSignatureFileTypes: string[];
    otherRisks: boolean;
    otherRisksFileTypes: string[];
    formula: boolean;
    formulaFileTypes: string[];
    formulaHyperlink: boolean;
    formulaHyperlinkFileTypes: string[];
};

export type FailProcessingState = {
    zeroByteFiles: 'skip' | 'block';
    unsupportedVersion: 'skip' | 'block';
    passwordProtected: 'skip' | 'block';
    invalidFileStructure: 'skip' | 'block';
};

export interface ScanOptions {
    multiscanning: boolean;
    deepCdr: boolean;
    adaptiveSandbox: boolean;
    proactiveDlp: boolean;
    unarchiving: boolean;
    privateScan: boolean;
}

export interface AnalysisFileCardProps {
}

export interface DragDropZoneProps {
    isUploaded: boolean;
    isLoading: boolean;
    fileName: string;
    fileSize: number;
    uploadProgress: number;
    onClick: () => void;
    onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    /** Matches `useRef<HTMLInputElement>(null)` / React 19 ref inference */
    fileInputRef: React.RefObject<HTMLInputElement | null>;
}
export interface ScanOptionComponentProps {
    scanOptions: ScanOptions;
    /** Defaults to [] when omitted */
    archivePasswords?: string[];
    onCheckboxChange: (option: string) => void;
    onArchivePasswordsChange: (passwords: string[]) => void;
    showArchivePasswords: boolean;
    onShowArchivePasswordsChange: (show: boolean) => void;
    isPrivateScanEnforced?: boolean;
    isPrivateScanAvailable?: boolean;
    isUnarchivingDisabled?: boolean;
}
